import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate as useRouterNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper, SortingState } from '@tanstack/react-table';

import AdminMobileMenuButton from 'components/admin/AdminMobileMenuButton';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DatePicker from 'components/shared/form/DatePicker';
import {
  MultiSelect as InfiniteMultiSelect,
  Select as InfiniteSelect,
} from 'components/shared/form/InfiniteScrollDropdown';
import Menu from 'components/shared/menu';
import Search from 'components/shared/Search';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import ControlledTable from 'components/shared/table/ControlledTable';
import CountMessage from 'components/shared/table/CountMessage';
import { useProfile } from 'context/profile';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import usePagination from 'hooks/usePagination';
import { Paginated } from 'models/Paginated';
import { PERMISSIONS } from 'models/Profile';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import EllipsisIcon from 'svg/EllipsisIcon';
import PlusIcon from 'svg/PlusIcon';

type PermissionKey = keyof typeof PERMISSIONS;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterType = 'dropdown' | 'multi_select' | 'checkbox' | 'date_range';

export interface FilterDef {
  key: string;
  label: string;
  type: FilterType;
  /** @deprecated Use fetchConfig instead for infinite scroll support. */
  fetchOptions?: (params: { search?: string; signal?: AbortSignal }) => Promise<{ data: any[] }>;
  fetchConfig?: {
    queryKey: readonly unknown[];
    fetchFn: (params: any, signal?: any) => Promise<any>;
    params?: Record<string, any>;
  };
  staticOptions?: any[];
  prependedOptions?: any[];
  getOptionLabel?: (option: any) => string;
  formatOptionLabel?: (option: any, meta?: any) => React.JSX.Element | string;
  getOptionValue?: (option: any) => string;
  toRequest?: (value: any) => string | string[] | undefined;
  defaultRange?: number;
}

export interface ColumnDef2<T> {
  id: string;
  header: string;
  render?: (row: T, ctx: ActionContext) => ReactNode;
  sortable?: boolean;
  size?: number;
}

export interface RowActionDef<T> {
  label: string;
  icon?: ReactNode;
  loadingIcon?: ReactNode;
  danger?: boolean;
  dividerBefore?: boolean;
  permission?: PermissionKey;
  visible?: (row: T, ctx: ActionContext) => boolean;
  disabled?: (row: T) => boolean;
  onClick: (row: T, ctx: ActionContext) => void | Promise<void>;
}

export interface ModalDef<T = any> {
  actionText: string;
  objType: string;
  subText?: string;
  onConfirm?: (row: T, ctx: ActionContext) => void | false | Promise<void | false>;
}

export interface CustomModalDef<T = any> {
  render: (row: T, show: boolean, onClose: () => void, ctx: ActionContext) => ReactNode;
}

export interface ActionContext {
  navigate: (path: string) => void;
  openModal: (name: string, row: any) => void;
  openSlideout: (name: string, row: any) => void;
  refetch: () => void;
  toast: (text: string) => void;
  profile: any;
}

// ---------------------------------------------------------------------------
// AdminListPageConfig
// ---------------------------------------------------------------------------

export interface AdminListPageConfig<T> {
  title: string;
  columnStorageKey?: string;
  queryKey: readonly unknown[];
  fetchFn: (params: Record<string, any>, signal?: AbortSignal) => Promise<Paginated<T>>;
  includes?: readonly string[];
  fetchParams?: {
    defaultSort: string;
    searchSort?: string;
    pageSize?: number;
    extra?: Record<string, any>;
  };
  searchPlaceholder?: string;
  showResultCount?: boolean;
  /**
   * Render checkbox filters (and the Clear Search/Filters link) on their own row below the
   * search/dropdown row. For pages whose filter bar is too crowded for a single row.
   */
  checkboxFilterRow?: boolean;
  addButton?: {
    label: string;
    permission: PermissionKey;
    path?: string;
    onClick?: (ctx: ActionContext) => void;
  };
  headerExtra?: ReactNode;
  filters?: FilterDef[];
  columns: ColumnDef2<T>[];
  rowActions?: RowActionDef<T>[];
  // Opt in to opening a row by clicking it. ControlledTable already supported this; it just was
  // not reachable from here. Use it INSTEAD of a row-action menu holding a single "open" item.
  onRowClick?: (row: T, ctx: ActionContext) => void;
  modals?: Record<string, ModalDef>;
  customModals?: Record<string, CustomModalDef<T>>;
  slideouts?: Record<
    string,
    {
      render: (row: T, onClose: () => void) => ReactNode;
      // Opt in to a shareable URL for this slideout: the open row's id becomes a path segment under
      // this base, so /admin/…/high-cost-medication-lists/<id> opens the panel and survives a refresh. Tabs that
      // omit these keep purely local state and are unaffected. The app must register a matching
      // `<tab path>/:id` route.
      routeBasePath?: string;
      // Resolves a row the loaded page doesn't contain. Required for a link to work cold, because
      // the list paginates and the linked row may sit on a page the browser never fetched.
      fetchRow?: (id: string) => Promise<T>;
    }
  >;
}

// ---------------------------------------------------------------------------
// Module-level filter cache — survives unmount, clears on page refresh (like Redux)
// ---------------------------------------------------------------------------

const filterCache = new Map<string, { filters: Record<string, any>; search: string }>();

function cacheKey(queryKey: readonly unknown[]) {
  return JSON.stringify(queryKey);
}

// ---------------------------------------------------------------------------
// Admin navigation tracker
//
// Records the previous admin path so a list can tell whether it's being re-entered
// from one of its own record pages. Called once in the persistent AdminGuard so it
// updates before the re-mounting list reads it, and clears retained filters when
// the user leaves the admin section entirely.
// ---------------------------------------------------------------------------

let previousAdminPath = '';
let currentAdminPath = '';

export function useAdminNavigationTracker() {
  const { pathname } = useLocation();
  if (pathname !== currentAdminPath) {
    previousAdminPath = currentAdminPath;
    currentAdminPath = pathname;
  }

  // AdminGuard unmounts only when navigating out of /admin — drop retained
  // filters so returning to admin later starts fresh.
  useEffect(() => {
    return () => {
      filterCache.clear();
      previousAdminPath = '';
      currentAdminPath = '';
    };
  }, []);
}

function cameFromOwnRecord(listPath: string) {
  return previousAdminPath.startsWith(`${listPath}/`);
}

function buildDefaultFilters(filters_: FilterDef[]) {
  const init: Record<string, any> = {};
  for (const f of filters_) {
    if (f.type === 'date_range') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (f.defaultRange ?? 90));
      init[f.key] = { start, end };
    } else {
      init[f.key] = f.type === 'multi_select' ? [] : f.type === 'checkbox' ? false : null;
    }
  }
  return init;
}

// ---------------------------------------------------------------------------
// useAdminListPage
// ---------------------------------------------------------------------------

export function useAdminListPage<T extends Record<string, any>>(config: AdminListPageConfig<T>) {
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const filters_ = config.filters ?? [];
  const key = cacheKey(config.queryKey);

  // Restore cached filters/search only when returning from one of this list's own
  // record pages; otherwise start fresh.
  const listPath = useLocation().pathname;
  const [restoreFromCache] = useState(() => cameFromOwnRecord(listPath));
  const cached = restoreFromCache ? filterCache.get(key) : undefined;

  // ---- Search state ----
  const [searchValue, setSearchValue] = useState(() => cached?.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // ---- Filter state ----
  const [filters, setFilters] = useState<Record<string, any>>(() => cached?.filters ?? buildDefaultFilters(filters_));

  // Persist to cache on change; not cleared on unmount so a record round-trip keeps
  // the filters. Resets on a full page refresh (like Redux).
  useEffect(() => {
    filterCache.set(key, { filters, search: searchValue });
  }, [key, filters, searchValue]);

  const setFilter = useCallback((key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(buildDefaultFilters(filters_));
    setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Build request params from filters ----
  const filtersForRequest = useMemo(() => {
    const req: Record<string, string | string[]> = {};
    if (debouncedSearch) req.search = debouncedSearch;
    for (const fDef of filters_) {
      const value = filters[fDef.key];
      if (fDef.toRequest) {
        const v = fDef.toRequest(value);
        if (v !== undefined) req[fDef.key] = v;
      } else if (fDef.type === 'date_range' && value && (value.start || value.end)) {
        // Always send both slots (e.g. ",2026-07-08") so the API can tell start from end
        // when one side is cleared.
        const toDateString = (d: Date | null | undefined) => (d ? d.toISOString().split('T')[0] : '');
        req[fDef.key] = `${toDateString(value.start)},${toDateString(value.end)}`;
      } else if (fDef.type === 'multi_select' && value?.length) {
        req[fDef.key] = value.map((v: any) => v.id).join(',');
      } else if (fDef.type === 'dropdown' && value?.id) {
        req[fDef.key] = value.id;
      } else if (fDef.type === 'checkbox' && value) {
        req[fDef.key] = 'true';
      }
    }
    return req;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearch]);

  // ---- Paging state ----
  const pagination = usePagination({ pageSize: config.fetchParams?.pageSize || 10 });
  const [sortBy, setSortBy] = useState<SortingState>(() => {
    const defaultSort = config.fetchParams?.defaultSort || '';
    if (!defaultSort) return [];
    const [id, dir] = defaultSort.split(' ');
    return [{ id, desc: dir === 'desc' }];
  });

  // Reset to page 0 when filters change
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersForRequest]);

  const sortByString = sortBy.length ? `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}` : '';
  const effectiveSort =
    debouncedSearch && config.fetchParams?.searchSort
      ? config.fetchParams.searchSort
      : sortByString || config.fetchParams?.defaultSort || '';

  // ---- Data fetching via React Query ----
  const queryParams = {
    ...filtersForRequest,
    page: pagination.paging.page,
    pageSize: pagination.paging.pageSize,
    sortBy: effectiveSort,
    ...(config.includes?.length ? { include: config.includes.join(',') } : {}),
    ...(config.fetchParams?.extra || {}),
  };

  const {
    data: queryResult,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...config.queryKey, queryParams],
    queryFn: ({ signal }) => config.fetchFn(queryParams, signal),
  });

  // Memoized because the fallback would otherwise be a fresh array on every render, and the
  // deep-link effect below depends on it.
  const data = useMemo(() => queryResult?.data ?? [], [queryResult]);
  const totalPages = queryResult?.meta?.totalPages ?? 0;
  const totalRecords = queryResult?.meta?.totalRecords ?? 0;

  const { pathname: slideoutPathname, search: slideoutSearch } = useLocation();
  const routerNavigate = useRouterNavigate();

  // ---- Modal state ----
  const [activeModal, setActiveModal] = useState<{ name: string; row: T } | null>(null);
  const openModal = useCallback((name: string, row: T) => setActiveModal({ name, row }), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  // ---- Slideout state ----
  const [activeSlideout, setActiveSlideout] = useState<{ name: string; row: T } | null>(null);

  // At most one slideout per tab opts into a URL; a second would need its own segment and there is
  // no case for two open at once.
  const routedSlideout = useMemo(() => {
    const entry = Object.entries(config.slideouts ?? {}).find(([, def]) => def.routeBasePath);

    return entry ? { name: entry[0], ...entry[1] } : null;
  }, [config.slideouts]);

  // The id is a path segment, not a query param — it identifies a resource, so it belongs in the
  // path. Read by trimming the tab's own base rather than through useParams, which would tie the
  // shared hook to each caller's route param name.
  const routedId = useMemo(() => {
    const base = routedSlideout?.routeBasePath;
    if (!base || !slideoutPathname.startsWith(`${base}/`)) return null;

    return slideoutPathname.slice(base.length + 1).split('/')[0] || null;
  }, [routedSlideout, slideoutPathname]);

  const setRoutedId = useCallback(
    (id: string | null) => {
      const base = routedSlideout?.routeBasePath;
      if (!base) return;

      // replace, not push: opening and closing a panel should not stack history entries the back
      // button has to walk through. The query string rides along so filters survive.
      routerNavigate({ pathname: id ? `${base}/${id}` : base, search: slideoutSearch }, { replace: true });
    },
    [routedSlideout, routerNavigate, slideoutSearch]
  );

  const openSlideout = useCallback(
    (name: string, row: T) => {
      setActiveSlideout({ name, row });
      if (routedSlideout?.name === name) setRoutedId((row as { id?: string }).id ?? null);
    },
    [routedSlideout, setRoutedId]
  );

  const closeSlideout = useCallback(() => {
    // For a routed slideout the URL is the single source of truth — clearing the id closes the
    // panel through the effect below. Clearing local state here as well would race the navigation:
    // between the two updates the effect sees no open panel but an id still in the URL, and
    // immediately reopens what was just closed.
    if (routedSlideout && activeSlideout?.name === routedSlideout.name) {
      setRoutedId(null);
      return;
    }

    setActiveSlideout(null);
  }, [routedSlideout, activeSlideout, setRoutedId]);

  // Keep an open slideout's row in step with the list it came from. The row is a snapshot taken
  // when the panel opened, so a mutation made inside the panel can invalidate the index, refetch
  // it, and still leave the panel rendering pre-mutation data.
  //
  // The open panel is read through the updater rather than from the effect's dependencies: naming
  // it as a dependency would re-run this on every state it sets, so a fetchFn that returns new row
  // objects on each refetch would keep re-entering. Returning the current state when nothing moved
  // makes the no-op case free.
  useEffect(() => {
    setActiveSlideout((current) => {
      const openRowId = (current?.row as { id?: string } | undefined)?.id;
      if (!openRowId) return current;

      const fresh = data.find((row) => (row as { id?: string }).id === openRowId);

      return fresh && fresh !== current!.row ? { name: current!.name, row: fresh } : current;
    });
  }, [data]);

  // Open from the URL: on a cold load the row usually isn't in `data`, since the linked record may
  // be on a page the browser never fetched — hence fetchRow. A link that no longer resolves drops
  // the param rather than erroring, leaving the plain tab.
  useEffect(() => {
    if (!routedSlideout) return;

    // Navigating away from the id — closing, or the back button — closes the panel.
    if (!routedId) {
      if (activeSlideout?.name === routedSlideout.name) setActiveSlideout(null);

      return;
    }

    if ((activeSlideout?.row as { id?: string } | undefined)?.id === routedId) return;

    const loaded = data.find((row) => (row as { id?: string }).id === routedId);
    if (loaded) {
      setActiveSlideout({ name: routedSlideout.name, row: loaded });
      return;
    }

    if (!routedSlideout.fetchRow) return;

    let cancelled = false;
    routedSlideout
      .fetchRow(routedId)
      .then((row) => {
        if (!cancelled) setActiveSlideout({ name: routedSlideout.name, row });
      })
      .catch(() => {
        if (!cancelled) setRoutedId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [routedSlideout, routedId, data, activeSlideout, setRoutedId]);

  return {
    profile,
    filters,
    searchValue,
    setSearchValue,
    setFilter,
    clearFilters,
    data,
    loading: isLoading,
    refetch,
    pagination,
    sorting: { sortBy, setSortBy },
    totalPages,
    totalRecords,
    activeModal,
    openModal,
    closeModal,
    activeSlideout,
    openSlideout,
    closeSlideout,
    addToast,
    queryParams,
  };
}

// ---------------------------------------------------------------------------
// <AdminListPageRenderer />
// ---------------------------------------------------------------------------

export interface TabDef {
  label: string;
  path: string;
  permission?: PermissionKey;
  flag?: string;
}

interface AdminAdminListPageRendererProps<T extends Record<string, any>> {
  config: AdminListPageConfig<T>;
  state: ReturnType<typeof useAdminListPage<T>>;
  tabs?: { pageTitle: string; tabDefs: TabDef[]; currentTabLabel: string };
}

export function AdminListPageRenderer<T extends Record<string, any>>({
  config,
  state,
  tabs,
}: AdminAdminListPageRendererProps<T>) {
  const navigate = useRouterNavigate();
  const {
    profile,
    filters,
    searchValue,
    setSearchValue,
    setFilter,
    data,
    loading,
    pagination,
    sorting,
    totalPages,
    totalRecords,
    activeModal,
    openModal,
    closeModal,
    activeSlideout,
    openSlideout,
    closeSlideout,
    clearFilters,
    refetch,
    addToast,
  } = state;

  const rowActions_ = config.rowActions ?? [];
  const modals_ = config.modals ?? {};
  const customModals_ = config.customModals ?? {};
  const slideouts_ = config.slideouts ?? {};

  const actionCtx: ActionContext = useMemo(
    () => ({
      navigate,
      openModal,
      openSlideout,
      refetch,
      toast: (text: string) => addToast({ text }),
      profile,
    }),
    [navigate, openModal, openSlideout, refetch, addToast, profile]
  );

  // ---- Build TanStack columns ----
  const columnHelper = useMemo(() => createColumnHelper<T>(), []);

  const columns = useMemo(() => {
    const cols: ColumnDef<T, any>[] = config.columns.map((colDef) =>
      columnHelper.accessor((row: T) => row, {
        id: colDef.id,
        header: colDef.header,
        cell: (info) => {
          if (colDef.render) return colDef.render(info.row.original, actionCtx);
          return (info.row.original as any)[colDef.id] ?? '';
        },
        enableSorting: colDef.sortable !== false,
        size: colDef.size,
      })
    );

    const visibleActions = rowActions_.filter((action) => {
      if (action.permission) return profile.has(PERMISSIONS[action.permission]);
      return true;
    });

    if (visibleActions.length > 0) {
      cols.push(
        columnHelper.display({
          id: 'actions',
          cell: (info) => (
            <RowActionMenu row={info.row.original} actions={rowActions_} ctx={actionCtx} profile={profile} />
          ),
          size: 50,
          enableSorting: false,
          meta: { style: { justifyContent: 'flex-end' } },
        })
      );
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.columns, rowActions_, profile, actionCtx]);

  // ---- Modal ----
  const modalDef = activeModal ? modals_[activeModal.name] : null;

  // ---- Has active filters? ----
  const hasActiveFilters = useMemo(() => {
    const defs = config.filters ?? [];
    return defs.some((fDef) => {
      const value = filters[fDef.key];
      if (fDef.type === 'multi_select') return value?.length > 0;
      if (fDef.type === 'checkbox') return !!value;
      return !!value?.id;
    });
  }, [filters, config.filters]);

  const showClearLink = hasActiveFilters || !!searchValue.trim();
  const inlineFilters = config.checkboxFilterRow
    ? config.filters?.filter((f) => f.type !== 'checkbox')
    : config.filters;
  const checkboxRowFilters = config.checkboxFilterRow
    ? config.filters?.filter((f) => f.type === 'checkbox')
    : undefined;

  // ---- Add button ----
  const addButton =
    config.addButton && profile.has(PERMISSIONS[config.addButton.permission]) ? (
      <Button
        onClick={() => {
          if (config.addButton!.onClick) {
            config.addButton!.onClick(actionCtx);
          } else if (config.addButton!.path) {
            navigate(config.addButton!.path);
          }
        }}>
        <PlusIcon width={14} height={14} />
        {config.addButton.label}
      </Button>
    ) : null;

  const headerWhite = !!tabs;

  return (
    <PageContainer>
      <HeaderWrapper $white={headerWhite}>
        <HeaderSection>
          {tabs ? (
            <Header>
              <TitleRow>
                <AdminMobileMenuButton />
                <Title>{tabs.pageTitle}</Title>
                {config.showResultCount && (
                  <HideUnder960>
                    <CountMessage count={totalRecords} countType='result' />
                  </HideUnder960>
                )}
              </TitleRow>
              <HeaderRight>
                {(addButton || config.headerExtra) && (
                  <ButtonGroup>
                    {config.headerExtra}
                    {addButton}
                  </ButtonGroup>
                )}
              </HeaderRight>
            </Header>
          ) : (
            <Header>
              <TitleRow>
                <AdminMobileMenuButton />
                <Title>{config.title}</Title>
                {config.showResultCount && (
                  <HideUnder960>
                    <CountMessage count={totalRecords} countType='result' />
                  </HideUnder960>
                )}
              </TitleRow>
              <HeaderRight>
                {(addButton || config.headerExtra) && (
                  <ButtonGroup>
                    {config.headerExtra}
                    {addButton}
                  </ButtonGroup>
                )}
              </HeaderRight>
            </Header>
          )}

          <FilterBar $compact={!!config.checkboxFilterRow}>
            {config.searchPlaceholder && (
              <SearchContainer>
                <Search placeholder={config.searchPlaceholder} value={searchValue} onChange={setSearchValue} />
              </SearchContainer>
            )}
            <FiltersGroup $count={inlineFilters?.length ?? 0}>
              {inlineFilters?.map((fDef) => (
                <FilterDropdown
                  key={fDef.key}
                  def={fDef}
                  value={filters[fDef.key]}
                  onChange={(v: any) => setFilter(fDef.key, v)}
                />
              ))}
              {!config.checkboxFilterRow && showClearLink && (
                <ClearFiltersLink onClick={clearFilters}>Clear Search/Filters</ClearFiltersLink>
              )}
            </FiltersGroup>
          </FilterBar>
          {config.checkboxFilterRow && (
            <CheckboxFilterRow data-testid='checkbox-filter-row' $indent={!!config.searchPlaceholder}>
              {checkboxRowFilters?.map((fDef) => (
                <FilterDropdown
                  key={fDef.key}
                  def={fDef}
                  value={filters[fDef.key]}
                  onChange={(v: any) => setFilter(fDef.key, v)}
                />
              ))}
              {showClearLink && <ClearFiltersLink onClick={clearFilters}>Clear Search/Filters</ClearFiltersLink>}
            </CheckboxFilterRow>
          )}
        </HeaderSection>

        {tabs && (
          <TabBarWrapper>
            {tabs.tabDefs
              .filter((t) => !t.permission || profile.has(PERMISSIONS[t.permission]))
              .filter((t) => !t.flag || profile.hasFlag(t.flag))
              .map((t) => (
                <Tab key={t.path} $active={t.label === tabs.currentTabLabel} onClick={() => navigate(t.path)}>
                  {t.label}
                </Tab>
              ))}
          </TabBarWrapper>
        )}
      </HeaderWrapper>

      <TableSection>
        <ControlledTable
          data={data}
          columns={columns}
          loading={loading}
          pageCount={totalPages}
          pagination={pagination}
          sorting={sorting}
          onRowClick={config.onRowClick ? (row) => config.onRowClick!(row.original, actionCtx) : undefined}
          showColumnToggle={!!config.columnStorageKey}
        />
      </TableSection>

      {modalDef &&
        activeModal &&
        (() => {
          const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
          const lowercase = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
          const modalTitle = `${capitalize(modalDef.actionText)} ${capitalize(modalDef.objType)}`;
          return (
            <StandardModal title={modalTitle} colorTheme={ColorThemeName.Danger} onCancel={closeModal}>
              <ModalContent>
                <BodySmall style={{ fontWeight: 500 }}>
                  Are you sure you want to {lowercase(modalDef.actionText)} this {modalDef.objType}?
                </BodySmall>
                {modalDef.subText && (
                  <BodySmall style={{ display: 'block', marginTop: 8 }}>{modalDef.subText}</BodySmall>
                )}
              </ModalContent>
              <Actions>
                <Button variant='ghost' onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  variant='danger'
                  onClick={async () => {
                    const result = modalDef.onConfirm
                      ? await modalDef.onConfirm(activeModal.row, actionCtx)
                      : undefined;
                    closeModal();
                    if (result !== false) refetch();
                  }}>
                  {modalTitle}
                </Button>
              </Actions>
            </StandardModal>
          );
        })()}

      {activeModal && customModals_[activeModal.name] && (
        <>{customModals_[activeModal.name].render(activeModal.row, true, closeModal, actionCtx)}</>
      )}

      {activeSlideout && slideouts_[activeSlideout.name] && (
        <>{slideouts_[activeSlideout.name].render(activeSlideout.row, closeSlideout)}</>
      )}
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// <RowActionMenu />
// ---------------------------------------------------------------------------

function RowActionMenu<T>({
  row,
  actions,
  ctx,
  profile,
}: {
  row: T;
  actions: RowActionDef<T>[];
  ctx: ActionContext;
  profile: any;
}) {
  const visibleActions = actions.filter((action) => {
    if (action.permission && !profile.has(PERMISSIONS[action.permission])) return false;
    if (action.visible && !action.visible(row, ctx)) return false;
    return true;
  });

  if (!visibleActions.length) return null;

  return (
    <Menu>
      <Menu.Trigger>
        <MenuTriggerButton data-testid='row-action-menu'>
          <EllipsisIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
        </MenuTriggerButton>
      </Menu.Trigger>
      <Menu.Content position='right' style={{ padding: 0, minWidth: 180 }}>
        {visibleActions.map((action) => {
          const isDisabled = action.disabled ? action.disabled(row) : false;
          return (
            <React.Fragment key={action.label}>
              {action.dividerBefore && <Menu.Divider />}
              <Menu.Item
                onClick={() => !isDisabled && action.onClick(row, ctx)}
                style={{
                  padding: '12px',
                  fontSize: '14px',
                  ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}>
                {(isDisabled && action.loadingIcon ? action.loadingIcon : action.icon) && (
                  <MenuIconWrapper>
                    {isDisabled && action.loadingIcon ? action.loadingIcon : action.icon}
                  </MenuIconWrapper>
                )}
                {action.danger ? <span style={{ color: colors.red100 }}>{action.label}</span> : action.label}
              </Menu.Item>
            </React.Fragment>
          );
        })}
      </Menu.Content>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// <FilterDropdown />
// ---------------------------------------------------------------------------

function FilterDropdown({ def, value, onChange }: { def: FilterDef; value: any; onChange: (v: any) => void }) {
  // Legacy eager-load path (deprecated fetchOptions)
  const [legacyOptions, setLegacyOptions] = useState<any[]>(def.staticOptions ?? []);
  useEffect(() => {
    if (def.staticOptions || def.fetchConfig || !def.fetchOptions) return;
    let cancelled = false;
    def.fetchOptions({}).then((res) => {
      if (!cancelled) setLegacyOptions(res.data);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New infinite scroll path
  const infiniteOpts = useInfiniteOptions(
    def.fetchConfig ?? {
      queryKey: ['__noop__'],
      fetchFn: () => Promise.resolve({ data: [], meta: { totalPages: 0, totalRecords: 0 }, links: {} }),
    }
  );

  const useInfinite = !!def.fetchConfig;
  const optionsProps = useInfinite ? infiniteOpts : { options: legacyOptions };

  const getLabel = def.getOptionLabel || ((o: any) => o.name || o.displayName || '');
  const getValue = def.getOptionValue || ((o: any) => o.id);

  if (def.type === 'checkbox') {
    return (
      <CheckboxFilterContainer>
        <CheckboxInput type='checkbox' checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <CheckboxLabel>{def.label}</CheckboxLabel>
      </CheckboxFilterContainer>
    );
  }

  if (def.type === 'multi_select') {
    return (
      <FilterContainer>
        <FilterLabel>{def.label}</FilterLabel>
        <InfiniteMultiSelect
          {...optionsProps}
          prependedOptions={def.prependedOptions}
          value={value || []}
          onChange={(v) => onChange(v)}
          getOptionValue={getValue}
          getOptionLabel={getLabel}
          placeholder='Select'
        />
      </FilterContainer>
    );
  }

  if (def.type === 'dropdown') {
    const allOption = { id: '', name: 'All' };
    const wrappedGetValue = (o: any) => (o === allOption ? '' : getValue(o));
    const wrappedGetLabel = (o: any) => (o === allOption ? 'All' : getLabel(o));

    return (
      <FilterContainer>
        <FilterLabel>{def.label}</FilterLabel>
        <InfiniteSelect
          {...optionsProps}
          prependedOptions={[allOption]}
          value={value || allOption}
          onChange={(v: any) => onChange(v === allOption ? null : v)}
          getOptionValue={wrappedGetValue}
          getOptionLabel={wrappedGetLabel}
          formatOptionLabel={def.formatOptionLabel}
          placeholder='All'
        />
      </FilterContainer>
    );
  }

  if (def.type === 'date_range') {
    return (
      <DateRangeContainer>
        <FilterContainer>
          <FilterLabel>{def.label} (Start)</FilterLabel>
          <DatePicker
            selected={value?.start ?? null}
            onChange={(date) => onChange({ ...value, start: date })}
            selectsStart
            startDate={value?.start}
            endDate={value?.end}
            maxDate={value?.end ?? new Date()}
          />
        </FilterContainer>
        <FilterContainer>
          <FilterLabel>{def.label} (End)</FilterLabel>
          <DatePicker
            selected={value?.end ?? null}
            onChange={(date) => onChange({ ...value, end: date })}
            selectsEnd
            startDate={value?.start}
            endDate={value?.end}
            minDate={value?.start}
            maxDate={new Date()}
          />
        </FilterContainer>
      </DateRangeContainer>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: auto;
`;

const HeaderWrapper = styled.div<{ $white: boolean }>`
  background: ${({ $white }) => ($white ? 'white' : 'transparent')};
`;

const HeaderSection = styled.div`
  padding: 24px 24px 0;
`;

const TableSection = styled.div`
  padding: 24px 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const HideUnder960 = styled.div`
  @media (max-width: 960px) {
    display: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FilterBar = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: flex-end;
  min-width: 0;
  margin-bottom: ${({ $compact }) => ($compact ? '12px' : '24px')};
`;

const SearchContainer = styled.div`
  flex: 0 0 250px;
  margin-right: 24px;
`;

const FiltersGroup = styled.div<{ $count: number }>`
  display: flex;
  align-items: flex-end;
  min-width: 0;
  flex: 1 1 auto;

  ${({ $count }) =>
    Array.from({ length: $count }, (_, i) => {
      const k = i + 1;
      return css`
        @media (max-width: ${500 + 120 * k - 1}px) {
          & > :nth-child(${k}) {
            display: none;
          }
        }
      `;
    })}
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 230px;
  min-width: 0;
  margin-right: 9px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  color: var(--black-75);
  margin-bottom: 4px;
`;

const CheckboxFilterContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 8px;
  min-height: 38px;
  margin-left: 16px;
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 9px;
`;

/* Second filter row (opt-in via checkboxFilterRow). Indented to start under the
   dropdowns when a search box occupies the left column (250px basis + 24px margin). */
const CheckboxFilterRow = styled.div<{ $indent: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 8px;
  min-width: 0;
  margin-bottom: 24px;
  ${({ $indent }) => $indent && 'margin-left: 274px;'}

  & > :first-child {
    margin-left: 0;
  }
`;

const ClearFiltersLink = styled.div`
  height: 38px;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  color: var(--primary-blue);
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  margin-left: 16px;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: 12px;
  color: var(--black-75);
  overflow-wrap: break-word;
  word-break: break-word;
`;

const TabBarWrapper = styled.div`
  display: flex;
  gap: 24px;
  padding: 0 24px;
  border-top: 1px solid var(--black-10);
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? 'var(--primary-blue)' : 'transparent')};
  padding: 8px 0;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active }) => ($active ? 'var(--primary-blue)' : 'var(--black-75)')};
  cursor: pointer;

  &:hover {
    color: var(--primary-blue);
  }
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.black5};
  }
`;

const MenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  margin-right: 8px;
  color: ${colors.black50};
`;
